import { TeamMember, Agent, Traveler } from '@marageti/z4-sdk/lib/people';
import { CountryCodeMap, TimeZone, CurrencyAmount } from '@marageti/z4-sdk/lib/standards';
import {
  PartnerOffice, Partner, TripMatch, ChecklistItem, TripMatchStatus,
} from '@marageti/z4-sdk/lib/travel';
import {
  formatPhoneNumber,
  getTimeZoneCity,
  getFormattedDateLongMonthYYYY,
  getTimezoneNameAndGMTOffset,
} from '@marageti/z4-lib';
import { RoleName } from '@marageti/z4-sdk/lib/auth';
import { differenceInDays, format, isAfter } from 'date-fns';
import { getMatchingUser } from '../auth';

export const getAgentRoles = (agent: Agent) => {
  const userInfo = getMatchingUser(agent.email, agent.userInfo);
  if (userInfo?.roles) {
    switch (userInfo.roles[0]) {
      case RoleName.AGENT: return 'Specialist';
      case RoleName.AGENT_ADMIN: return 'Manager';
      case RoleName.AGENT_SUPPORT: return 'Support';
      default: return '-';
    }
  }
  return '-';
};

export const getFormattedPartnerCountries = (partner: Partner, countryCodes: CountryCodeMap) => {
  let str = '';
  if (partner?.primaryCountries?.length > 0) {
    str += countryCodes[partner.primaryCountries[0].country];
  }
  if (partner?.secondaryCountries?.length > 0) {
    str += ' · ';
    str += countryCodes[partner.secondaryCountries[0].country];
  }
  return str;
};

export const formatCountryCodes = (countryCodes: string[], countryCodeMap: CountryCodeMap) => {
  let countryNames: string[] = [];
  if (countryCodes) {
    countryNames = countryCodes.map((c) => countryCodeMap[c]);
  }
  return countryNames.join(', ');
};

export const getPhoneNumber = (person: Agent | TeamMember | Traveler | undefined): string | undefined => {
  if (!person) return undefined;
  if (!person.phone
    || !person.phone.number) {
    return '-';
  }
  return formatPhoneNumber(person.phone.prefix, person.phone.number) || '-';
};

export const getPartnerUSPhoneNumber = (partner: Partner | undefined): string | undefined => {
  if (!partner) return undefined;
  if (!partner.usPhone
    || !partner.usPhone.number) {
    return '-';
  }
  return formatPhoneNumber(partner.usPhone.prefix, partner.usPhone.number);
};

export const getPartnerPrimaryPhoneNumber = (partner: Partner | undefined): string | undefined => {
  if (!partner) return undefined;
  if (!partner.primaryPhone
    || !partner.primaryPhone.number) {
    return '-';
  }
  return formatPhoneNumber(partner.primaryPhone.prefix, partner.primaryPhone.number);
};

export const getPartnerSMSPhoneNumber = (partner: Partner | undefined): string | undefined => {
  if (!partner) return undefined;
  if (!partner.smsPhoneNumber) {
    return '-';
  }
  return formatPhoneNumber('', partner.smsPhoneNumber) || '-';
};

export const getPartnerOfficePhoneNumber = (office: PartnerOffice | undefined): string | undefined => {
  if (!office) return undefined;
  if (!office.phone
    || !office.phone.number) {
    return '-';
  }
  return formatPhoneNumber(office.phone.prefix, office.phone.number);
};

export const getTimeZone = (person: Agent | Traveler | TeamMember | undefined): string | undefined => {
  if (!person) return undefined;
  if (!person.timeZone?.name || typeof person.timeZone.rawOffsetInMinutes !== 'number') {
    return '-';
  }
  return getTimezoneNameAndGMTOffset(person.timeZone.name, person.timeZone.rawOffsetInMinutes);
};

export const formatTimeWithTimeZoneName = (timeZone: TimeZone | undefined, time?: string): string => {
  if (!timeZone || !timeZone.name || timeZone.name === '') return '-';
  const city = getTimeZoneCity(timeZone.name);
  return `${city} (Currently ${time || '-'})`;
};

export const getPersonTime = (person: Agent | Traveler | TeamMember | undefined, time?: string): string | undefined => {
  if (!person) return undefined;
  return formatTimeWithTimeZoneName(person.timeZone, time);
};

export const getHistory = (person: Agent | Traveler | TeamMember | undefined): string | undefined | any => {
  if (!person) return undefined;
  if (!person.createdAt) return '-';
  return getFormattedDateLongMonthYYYY(person.createdAt);
};

export const getCountryOfResidence = (traveler: Traveler | undefined): string | undefined => {
  if (!traveler) return undefined;
  if (!traveler.countryOfResidence) return '-';
  return traveler.countryOfResidence;
};

export const getRatingNumber = (ratingPercentage: number | undefined): number => {
  if (!ratingPercentage) return 0;
  return Math.round(ratingPercentage / 20);
};

export const titleCase = (str: string) => {
  const splitStr = str.toLowerCase().split(' ');
  for (let i = 0; i < splitStr.length; i += 1) {
    splitStr[i] = splitStr[i].charAt(0).toUpperCase() + splitStr[i].substring(1);
  }
  return splitStr.join(' ');
};

// This function compares the full names of two objects, and it used for sorting
// a list of names into alphabetical order
// The objects are expected to have a 'person' property with a 'fullName' string.
// The function returns -1 if the full name of the first object is alphabetically
// less than the full name of the second object, 1 if the full name of the first
// object is alphabetically greater than the full name of the second object,
// and 0 if the full names are equal.

// ***This function was refactored with ChatGPT.***
export const compareNames = (
  a: { fullName: string },
  b: { fullName: string },
) => (a.fullName.localeCompare(b.fullName));

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

/** Deadline for the first message in UTC ms: +24h Sun–Thu, +72h Fri, +48h Sat so windows land on weekdays. */
export const getFirstMessageDeadlineMs = (createdAt: string): number => {
  const creationTime = new Date(createdAt);
  const utcDay = creationTime.getUTCDay();
  let addMs: number;
  if (utcDay <= 4) {
    addMs = DAY_MS;
  } else if (utcDay === 5) {
    addMs = 3 * DAY_MS;
  } else {
    addMs = 2 * DAY_MS;
  }
  return creationTime.getTime() + addMs;
};

export const calculateRemainingTime = (createdAt: string): number => {
  const deadlineMs = getFirstMessageDeadlineMs(createdAt);
  const remainingMilliseconds = deadlineMs - Date.now();
  if (remainingMilliseconds <= 0) return 0;

  return Math.ceil(remainingMilliseconds / HOUR_MS);
};

/**
 * Formats and returns a string representing the currency details based on the provided currency amount.
 *
 * @param {CurrencyAmount} currencyAmount - Object containing the amount, currency code, exchange rate, and timestamp.
 * @returns {string} A formatted string with the currency amount, corresponding USD amount, exchange rate, and date.
 */
export const getCurrencyDetailsContent = (currencyAmount: CurrencyAmount) => {
  const formatCurrency = (amount: number, currencyCode: string) => new Intl.NumberFormat('en-US', { style: 'currency', currency: currencyCode }).format(amount);
  const formatDate = (timestamp: string) => format(new Date(timestamp), 'MMM d, yyyy');

  const USDAmount = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(currencyAmount.amountUSD);
  switch (currencyAmount.currencyCode) {
    case 'EUR': {
      const reverseExchangeRate = 1 / currencyAmount.exchangeRate;
      const EURAmount = formatCurrency(currencyAmount.amount, currencyAmount.currencyCode).replace(/[.,]/g, ((match) => (match === ',' ? '.' : ',')));
      return `${EURAmount} (${USDAmount} at €1 = $${reverseExchangeRate.toFixed(2)} on ${formatDate(currencyAmount.exchangeRateTimestamp)})`;
    }
    case 'GBP': {
      const reverseExchangeRate = 1 / currencyAmount.exchangeRate;
      const GBPAmount = formatCurrency(currencyAmount.amount, currencyAmount.currencyCode);
      return `${GBPAmount} (${USDAmount} at £1 = $${reverseExchangeRate.toFixed(2)} on ${formatDate(currencyAmount.exchangeRateTimestamp)})`;
    }
    default:
      return USDAmount;
  }
};

export const getShowRequestReview = (tripMatch?: TripMatch) => {
  if (!tripMatch) {
    return false;
  }

  const isMatchSold = tripMatch.status === TripMatchStatus.MatchSold;
  const needsReviewRequest = tripMatch.checklist?.includes(ChecklistItem.NeedsZicassoReviewRequest);
  const returnDate = new Date(tripMatch.returnDate);
  const isPastReturnDate = isAfter(new Date(), returnDate);

  const isWithinLast91Days = differenceInDays(new Date(), returnDate) <= 91;

  return isMatchSold && needsReviewRequest && isPastReturnDate && isWithinLast91Days;
};

/**
 * Formats adult and child age groups into a readable string format.
 * Example output: "Traveler Age Groups: Adults (18-31), (65+) and Children (8-12)"
 *
 * @param adultAgeGroups - Array of adult age ranges (e.g., ['18-31', '65+']) or null/undefined
 * @param childAgeGroups - Array of child age ranges (e.g., ['8-12', '13-17']) or null/undefined
 * @returns A formatted string containing the age groups, or an empty string if no age groups are provided
 */
export const formatAgeGroups = (adultAgeGroups?: string[] | null, childAgeGroups?: string[] | null): string => {
  const parts: string[] = [];

  if (adultAgeGroups?.length) {
    const adultGroups = adultAgeGroups.map((group) => `(${group})`).join(', ');
    parts.push(`Adults ${adultGroups}`);
  }

  if (childAgeGroups?.length) {
    const childGroups = childAgeGroups.map((group) => `(${group})`).join(', ');
    parts.push(`Children ${childGroups}`);
  }

  return parts.length ? `Traveler Age Groups: ${parts.join(' and ')}` : '';
};
